import {useEffect, useRef, useCallback, useState} from "react";
import io from "socket.io-client";

import {crud} from "../crudRequests";
import {chatSocketOptions} from "../features/messaging/chatHandshake";

// `tenant` joins the tenant-scoped room alongside the legacy bare-pid one.
// Patient ids are NOT unique across tenants — 593 are shared between
// havenpointhealth, canadianhealthlabs and internal — so a room named for the
// bare pid is joined by two different people. New server events publish only to
// `t:<db>:<pid>`; the legacy room stays until every emitter has moved.
export function useSocket(url, room, dispatch, tenant) {
  const socketRef = useRef({});

  const updateAppt = useCallback(async function (data, {ntf, changingDoctor}) {
    dispatch &&
      dispatch({type: "UPDATE_APPOINTMENT", payload: data, changingDoctor});
    console.log("updateAppt");
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  const updatPt = useCallback(async function (pid, payload, options) {
    dispatch && dispatch({type: "UPDATE_PATIENT", pid, payload, options});
  }, []);

  const newAppt = useCallback(async function (data, {ntf, updatePatient}) {
    dispatch &&
      dispatch({
        type: "NEW_APPOINTMENT",
        payload: data,
        updatePatient,
      });

    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  const newFormSubmission = useCallback(async function (data, {ntf}) {
    dispatch &&
      dispatch({
        type: "NEW_FORM_SUBMISSION",
        payload: data,
      });
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);
  const completedForm = useCallback(async function ({ntf}) {
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  const ccAdded = useCallback(async function ({ntf}) {
    // if (ntf) dispatch({type: "NEW_NOTIFICATION", payload: ntf});
  }, []);

  // One form of this patient's changed. The payload deliberately carries no
  // answers — only enough to know the list is stale — so this re-reads rather
  // than patching state from the wire.
  const formSubmissionChanged = useCallback(function (payload) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("brightlight:form-submission-changed", {detail: payload})
      );
    }
  }, []);

  useEffect(() => {
    if (room) {
      const rooms = tenant ? [room, `t:${tenant}:${room}`] : [room];
      // The session cookie travels with the connection, so the server can tell
      // who is listening and which clinic's rooms are theirs (S13).
      socketRef.current = io(url, {
        withCredentials: true,
        auth: {
          rooms,
        },
      });

      socketRef.current.on("update_appt", updateAppt);
      socketRef.current.on("update_pt", updatPt);
      socketRef.current.on("new_appt", newAppt);
      socketRef.current.on("new_form_submission", newFormSubmission);
      socketRef.current.on("completed_form", completedForm);
      socketRef.current.on("cc_added", ccAdded);
      socketRef.current.on("form_submission_changed", formSubmissionChanged);

      return () => {
        socketRef.current.off("update_appt");
        socketRef.current.off("new_appt");
        socketRef.current.off("update_pt");
        socketRef.current.off("new_form_submission");
        socketRef.current.off("completed_form");
        socketRef.current.off("cc_added");
        socketRef.current.off("form_submission_changed");
        socketRef.current.disconnect();
      };
    }
  }, [room, updateAppt]);

  return socketRef;
}

export function useChatSocket(url, rooms, patient, dispatch, handlers = {}) {
  const socketRef = useRef({});
  // Read through refs so the socket is opened once and always sees the
  // caller's latest rooms and handlers. The server joins a conversation room
  // only on `join-room`, so the hook joins `rooms` on every connect.
  const roomsRef = useRef(rooms);
  const handlersRef = useRef(handlers);
  roomsRef.current = rooms;
  handlersRef.current = handlers;

  const joinRooms = useCallback(function () {
    (roomsRef.current || []).forEach((id) => {
      if (id) socketRef.current.emit("join-room", id);
    });
  }, []);

  const newMessge = useCallback(async function (data) {
    const {db, ...msg} = data || {};

    dispatch({
      type: "NEW_MESSAGE",
      payload: msg,
    });
    handlersRef.current?.onMessage?.(msg);
  }, []);

  const userStatus = useCallback(async function ({userId, status}) {
    dispatch({
      type: "SET_USER_STATUS",
      payload: {userId, status},
    });
  }, []);

  const userCurrentStatus = useCallback(async function (status) {
    dispatch({
      type: "SET_USERS_CURRENT_STATUS",
      payload: status,
    });
  }, []);

  const writingStatus = useCallback(async function ({chatId, userId}) {
    dispatch({
      type: "WRITING",
      payload: {chatId, userId},
    });
  }, []);

  // A refused send `{code, conversationId, clientMessageId}` (D81).
  const messageError = useCallback(function (payload) {
    handlersRef.current?.onMessageError?.(payload);
  }, []);

  const conversationChanged = useCallback(function (payload) {
    if (payload?.reason === "message_deleted") {
      dispatch({
        type: "MESSAGE_DELETED",
        payload: {messageId: payload.messageId, conversationId: payload.conversationId},
      });
    }
    handlersRef.current?.onConversationChanged?.(payload);
  }, []);

  useEffect(() => {
    if (rooms || patient?.pid) {
      socketRef.current = io(url, chatSocketOptions({rooms, userId: patient?.pid}));

      socketRef.current.on("connect", joinRooms);
      socketRef.current.on("message", newMessge);
      socketRef.current.on("user-status", userStatus);
      socketRef.current.on("writing", writingStatus);
      socketRef.current.on("users-current-status", userCurrentStatus);
      socketRef.current.on("message-error", messageError);
      socketRef.current.on("conversation:changed", conversationChanged);

      return () => {
        socketRef.current.off("connect", joinRooms);
        socketRef.current.off("message", newMessge);
        socketRef.current.off("user-status", userStatus);
        socketRef.current.off("writing", writingStatus);
        socketRef.current.off("users-current-status", userCurrentStatus);
        socketRef.current.off("message-error", messageError);
        socketRef.current.off("conversation:changed", conversationChanged);
        socketRef.current.disconnect();
      };
    }
  }, [url, patient?.pid]);

  // A new conversation id (a draft created on its first send) is joined on the
  // open socket; a socket still connecting joins it on "connect".
  useEffect(() => {
    if (socketRef.current?.connected) joinRooms();
  }, [rooms]);

  return socketRef;
}

export function useWindowDimensions() {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return dimensions;
}
